/**
 * Desktop UHF encoder abstraction.
 *
 * The only thing that knows about the bridge protocol is
 * `ThyWebSocketEncoderClient`. UI and services depend on this interface so the
 * transport can be swapped (or mocked) without touching encode logic.
 */

export type EncoderConnectionStatus = 'disconnected' | 'connecting' | 'connected'

export interface EncoderDeviceInfo {
  serial: string | null
  firmware: string | null
  hardware: string | null
  /** 0 = Answer mode. Anything else and the reader will not behave predictably. */
  workMode: number | null
  rfPower: number | null
}

export interface EncoderFrameLogEntry {
  at: string
  direction: 'sent' | 'recv'
  ops?: string
  raw: string
}

export type EncoderFailureCode =
  | 'bridge_unreachable'
  | 'device_busy'
  | 'bridge_duplicate'
  | 'work_mode'
  | 'no_tag'
  | 'read_failed'
  | 'write_failed'
  | 'protocol'

export class EncoderError extends Error {
  readonly code: EncoderFailureCode

  constructor(code: EncoderFailureCode, message: string) {
    super(message)
    this.name = 'EncoderError'
    this.code = code
  }
}

export interface UhfEncoderClient {
  connect(): Promise<EncoderDeviceInfo>
  disconnect(): Promise<void>
  getStatus(): EncoderConnectionStatus
  getDeviceInfo(): EncoderDeviceInfo | null
  onStatusChange(listener: (status: EncoderConnectionStatus) => void): () => void
  /** Optional: raw bridge transcript for diagnostics. Absent on the mock client. */
  getFrameLog?(): EncoderFrameLogEntry[]
  /** TID bank 2, ptr 0, 6 words. Also proves a tag is present. */
  readTid(): Promise<string>
  /** PC word: EPC bank 1, ptr 1, 1 word. */
  readPc(): Promise<string>
  /** EPC bank 1, ptr 2, `words` words. */
  readEpc(words: number): Promise<string>
  /** WriteEPCG2 only — WriteCardG2 truncates and corrupts the PC word. */
  writeEpc(epcHex: string, words: number): Promise<void>
}
